/**
 * Booking Service API
 * Frontend API calls for booking operations
 */
import axios from 'axios';
import config from 'config';

const API_URL = config.API_URL;
const API = `${API_URL}/api`;

// Create axios instance
const apiClient = axios.create({
  baseURL: API,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests if available
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Booking API
export const bookingAPI = {
  // Get booking options (vehicles, services, options)
  getOptions: async () => {
    const response = await apiClient.get('/bookings/options');
    return response.data;
  },

  // Get public pricing
  getPricing: async () => {
    const response = await apiClient.get('/settings/public/pricing');
    return response.data;
  },

  // Get service hours
  getServiceHours: async () => {
    const response = await apiClient.get('/settings/public/service-hours');
    return response.data;
  },

  // Estimate price
  estimatePrice: async (params) => {
    const response = await apiClient.post('/bookings/estimate', null, { params });
    return response.data;
  },

  // Validate booking data
  validateBooking: async (bookingData) => {
    const response = await apiClient.post('/bookings/validate', bookingData);
    return response.data;
  },

  // Create booking
  createBooking: async (bookingData) => {
    const response = await apiClient.post('/bookings/', bookingData);
    return response.data;
  },

  // Confirm booking (accept CGV)
  confirmBooking: async (bookingId) => {
    const response = await apiClient.post(`/bookings/${bookingId}/confirm`);
    return response.data;
  },

  // Get booking by ID
  getBooking: async (bookingId) => {
    const response = await apiClient.get(`/bookings/${bookingId}`);
    return response.data;
  },

  // Get my bookings
  getMyBookings: async (page = 1, perPage = 10) => {
    const response = await apiClient.get('/bookings/my/list', {
      params: { page, per_page: perPage }
    });
    return response.data;
  },
};

// Pricing API (LOT 3 - Moteur de Tarification)
export const pricingAPI = {
  // Calculate real price
  calculatePrice: async (priceInput) => {
    const response = await apiClient.post('/pricing/calculate', priceInput);
    return response.data;
  },

  // Get zones
  getZones: async (activeOnly = true) => {
    const response = await apiClient.get('/pricing/zones', {
      params: { active_only: activeOnly }
    });
    return response.data;
  },

  // Get pricing rules
  getRules: async (activeOnly = true) => {
    const response = await apiClient.get('/pricing/rules', {
      params: { active_only: activeOnly }
    });
    return response.data;
  },

  // Get active pricing version
  getVersion: async () => {
    const response = await apiClient.get('/pricing/version');
    return response.data;
  },
};

export default bookingAPI;
